import React, { memo, useMemo, Fragment } from 'react';
import { Container, Row, Col, CardBody, Card } from 'reactstrap';
import { HTMLService } from '@business/services';
import { useRouteParam, useHistory } from '@core/router';
import { useBloc } from '@core/utils/bloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { Link } from 'react-router-dom';
import './index.scss';
import { CBreadcrumb } from '@modules/common/Breadcrumb';
import parse from 'html-react-parser';
import { GCPaginate } from '@core/components/GCPaginate';
import { HomePageBloc } from './HomePageBloc';
import { FilteredHelpArticleResponse } from '@business/entities/frontend';
import { LoadingIndicator } from '@core/components/LoadingIndicator';
import { tap } from 'rxjs/operators';

interface ArticleListingProps {
  articles: FilteredHelpArticleResponse[];
}

const ArticleListing = memo((props: ArticleListingProps) => {
  const { articles } = props;
  const history = useHistory();
  return (
    <Row>
      <Col md={12} lg={12}>
        <ul className="list-unstyled card_list-category">
          {articles.map(article => {
            let breadcrumbs = article.breadcrumb;
            breadcrumbs.pop();
            breadcrumbs = breadcrumbs.map(b => {
              return { title: b.title, onClick: () => history.push(b.link ?? '') };
            });
            const content = article.content ? parse(HTMLService.strip(article.content)) : '';
            return (
              <Fragment key={article.id}>
                <li>
                  <h5 className="text-truncate font-size-15">
                    <Link to={article.frontLink} className="text-dark">
                      {article!.title}
                    </Link>
                  </h5>
                  {content !== '' && <div className="text-muted">{content}</div>}
                  {breadcrumbs.length > 1 && <CBreadcrumb items={breadcrumbs} />}
                </li>
                <hr />
              </Fragment>
            );
          })}
        </ul>
      </Col>
    </Row>
  );
});

export const HomePage = memo(() => {
  const company = useRouteParam('company', true);
  const searchQuery = window.location.search;
  const bloc = useBloc(HomePageBloc, company, searchQuery);
  const articles = useObservable(bloc.articles$);
  const pageMeta = useObservable(bloc.paginated.pageMeta$);
  const loading = useLoadingState(bloc.loading);

  const showArticles = useMemo(() => (articles ? !!articles.length : false), [articles]);
  if (!articles || loading.loading) return <TopLoadingBar />;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>
              {showArticles ? (
                <ArticleListing articles={articles} />
              ) : (
                <div className="row">
                  <div className="col">
                    <div className="empty-cart text-center">
                      <span className="icon">
                        <i className="fa fa-tags font-size-24 mb-2"></i>
                      </span>
                      <div className="details">
                        <h4 className="heading">No help article found</h4>
                        <p className="text">There are no article to display on this screen</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
          {loading.refreshing && (
            <div className="mb-4">
              <LoadingIndicator />
            </div>
          )}
          {!!articles.length && (
            <GCPaginate
              total={pageMeta!.total}
              perPage={pageMeta!.per_page}
              handlePageClick={page =>
                bloc.paginated
                  .patchQuery({ page })
                  .pipe(tap(() => window.scrollTo(0, 0)))
                  .subscribe()
              }
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
});
